import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Crown, Loader2, Users } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { usePoll } from '@/hooks/use-realtime'
import { usePresence } from '@/hooks/use-presence'
import { featureOn } from '@/lib/features'
import {
  inviteLine,
  LOBBY_KINDS,
  lobbyKind,
  pickInvite,
  type LobbyKind,
  type LobbyWithMembers,
} from '@/lib/lobby'
import type { Lobby, LobbyMember, Profile } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * The lobby: "Bob's starting X, want to join?" and the waiting room behind it
 * (migrations/0061_lobbies.sql). Built for Team Missile Defence and asked for
 * as a common piece, so nothing in here knows what the lobby is FOR — a kind
 * maps to a title and a page in lib/lobby.ts, and the page owns what happens
 * once the host starts.
 *
 * Three exports:
 *
 *   LobbyInvite   the popup, mounted on the app shell so it reaches people
 *                 wherever they are
 *   useLobby      one lobby and its members, kept current
 *   LobbyRoom     the waiting room: who is in, the seats left, and the host's
 *                 Start
 *
 * ## Why the popup polls and the room uses presence
 *
 * Same split as PresentationInvite, for the same reason: a would-be player does
 * not know the lobby id, so there is no presence channel for them to be on, and
 * the row in the database is what makes a poll correct. Inside the lobby
 * everybody DOES know the id, so the room carries the peer signal Discussion
 * uses — a write bumps a timestamp in presence, everybody else refetches — and
 * the host pressing Start reaches every screen in a fraction of a second rather
 * than at the next poll. Neither path uses postgres_changes or broadcast; both
 * deliver nothing on this platform (see use-presence.ts).
 */

const DISMISS_KEY = 'teamaccel-lobby-invites-dismissed'

function readDismissed(): string[] {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function pushDismissed(lobbyId: string) {
  try {
    const next = [...readDismissed().filter((s) => s !== lobbyId), lobbyId].slice(-30)
    localStorage.setItem(DISMISS_KEY, JSON.stringify(next))
  } catch {
    /* best effort */
  }
}

type Names = Record<string, string>

async function loadNames(userIds: string[]): Promise<Names> {
  if (userIds.length === 0) return {}
  const { data } = await sb().from('profiles').select('user_id, display_name, email').in('user_id', userIds)
  const names: Names = {}
  for (const p of (data ?? []) as Pick<Profile, 'user_id' | 'display_name' | 'email'>[]) {
    names[p.user_id] = p.display_name || p.email
  }
  return names
}

/**
 * The popup. Lasts as long as the lobby is open and has a seat: the host
 * pressing Start is what takes it away, on everybody's next poll.
 *
 * 15s, the same interval as PresentationInvite and for the same reason — this
 * runs on every page for every signed-in member, against a PostgREST that
 * answers 503 when the shared database runs out of connections. A lobby stays
 * open until the host is ready, so arriving fifteen seconds late to it costs
 * nothing. It does not poll at all for a team with no lobby kind switched on.
 */
export function LobbyInvite() {
  const { currentTeam, session } = useAuth()
  const teamId = currentTeam?.id
  const userId = session?.user?.id
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const enabled = React.useCallback((k: LobbyKind) => featureOn(currentTeam, k.feature), [currentTeam])
  const anyOn = LOBBY_KINDS.some(enabled)

  const [dismissed, setDismissed] = React.useState<string[]>(() => readDismissed())
  const [joining, setJoining] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const { data, refetch } = useQuery(async () => {
    if (!teamId || !anyOn) return null
    const { data: rows, error: err } = await sb()
      .from('lobbies')
      .select('*, lobby_members(user_id)')
      .eq('team_id', teamId)
      .eq('status', 'open')
      .order('created_at', { ascending: false })
    if (err) throw err
    const lobbies = (rows ?? []) as LobbyWithMembers[]
    return { lobbies, names: await loadNames([...new Set(lobbies.map((l) => l.host_id))]) }
  }, [teamId, anyOn])

  usePoll(() => void refetch(), 15_000, Boolean(teamId && anyOn))

  const invite = pickInvite(data?.lobbies ?? [], {
    userId,
    dismissed,
    now: Date.now(),
    pathname,
    enabled,
  })
  const kind = invite ? lobbyKind(invite.kind) : undefined

  // A different invite is a different question; an error about the last one
  // does not belong on it.
  React.useEffect(() => setError(null), [invite?.id])

  function decline() {
    if (!invite) return
    pushDismissed(invite.id)
    setDismissed(readDismissed())
  }

  async function join() {
    if (!invite || !kind) return
    setJoining(true)
    const { error: err } = await sb().rpc('join_lobby', { p_lobby_id: invite.id })
    setJoining(false)
    // Full, or the host started while this was on screen. Say so and let the
    // next poll take the popup away rather than navigating into a locked game.
    if (err) return setError(err.message)
    navigate(kind.path(invite.id))
  }

  if (!invite || !kind || !data) return null

  return (
    <Dialog open onOpenChange={(o) => !o && decline()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" /> {kind.title}
          </DialogTitle>
          <DialogDescription>{inviteLine(data.names[invite.host_id] ?? null, kind.title)}</DialogDescription>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          {invite.lobby_members.length} of {invite.max_players} in so far.
        </p>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={decline}>
            No
          </Button>
          <Button onClick={() => void join()} disabled={joining}>
            {joining && <Loader2 className="h-4 w-4 animate-spin" />} Yes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export interface LobbyState {
  lobby: Lobby
  members: LobbyMember[]
  names: Names
}

/**
 * One lobby, its members and their names, kept current by the peer signal
 * plus a poll. `signal()` is for the caller to raise after any write it makes
 * — a signal is raised by a WRITE, never by hearing about one (discussion.tsx).
 *
 * The poll stays at 4s until presence has proven itself and 15s after: a write
 * from a tab that has since closed bumps nobody, and the poll is what sees it.
 */
export function useLobby(lobbyId: string | undefined) {
  const { session } = useAuth()
  const userId = session?.user?.id

  const query = useQuery(async (): Promise<LobbyState | null> => {
    if (!lobbyId) return null
    const { data, error } = await sb()
      .from('lobbies')
      .select('*, lobby_members(*)')
      .eq('id', lobbyId)
      .maybeSingle()
    if (error) throw error
    if (!data) return null
    const { lobby_members: embedded, ...lobby } = data as Lobby & { lobby_members: LobbyMember[] }
    // An embed comes back in no particular order; seats fill in the order
    // people said yes, and must not swap places between polls.
    const members = [...embedded].sort((a, b) => a.joined_at.localeCompare(b.joined_at))
    return { lobby, members, names: await loadNames(members.map((m) => m.user_id)) }
  }, [lobbyId])
  const { refetch } = query

  const [myBump, setMyBump] = React.useState(0)
  const myBumpRef = React.useRef(0)
  myBumpRef.current = myBump
  const signal = React.useCallback(() => setMyBump(Date.now()), [])

  const presenceState = React.useMemo(() => ({ bump: myBump }), [myBump])
  const peers = usePresence<{ bump: number }>({
    channel: `lobby-peers-${lobbyId ?? 'none'}`,
    key: userId ?? 'anon',
    state: presenceState,
    enabled: Boolean(lobbyId && userId),
  })

  const latestBump = peers.peers.reduce((max, p) => Math.max(max, p.bump ?? 0), 0)
  const seenBump = React.useRef(0)
  React.useEffect(() => {
    if (latestBump <= seenBump.current) return
    seenBump.current = latestBump
    if (latestBump === myBumpRef.current) return
    void refetch()
  }, [latestBump, refetch])

  usePoll(() => void refetch(), peers.live ? 15_000 : 4_000, Boolean(lobbyId))

  return { ...query, signal }
}

/**
 * The waiting room. Renders while the lobby is open; the page decides what to
 * show once it is not.
 *
 * Every button here is courtesy — 0061's functions are what refuse a second
 * Start, a sixth player, or a non-host pressing Start.
 */
export function LobbyRoom({
  state,
  title,
  onChanged,
  children,
}: {
  state: LobbyState
  title: string
  /** Called after any write, so the page can refetch and raise the signal. */
  onChanged: () => void
  /** What the game is, for whoever is deciding whether to join. */
  children?: React.ReactNode
}) {
  const { session } = useAuth()
  const userId = session?.user?.id
  const navigate = useNavigate()
  const { lobby, members, names } = state
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const isHost = lobby.host_id === userId
  const isIn = members.some((m) => m.user_id === userId)
  const full = members.length >= lobby.max_players
  const seats = Array.from({ length: lobby.max_players }, (_, i) => members[i] ?? null)

  async function call(fn: string, after?: () => void) {
    setBusy(true)
    setError(null)
    const { error: err } = await sb().rpc(fn, { p_lobby_id: lobby.id })
    setBusy(false)
    if (err) return setError(err.message)
    onChanged()
    after?.()
  }

  return (
    <Card className="mx-auto max-w-lg">
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>
          {isHost
            ? 'Your team has been asked to join. Start when you have enough — nobody can join after that.'
            : `${names[lobby.host_id] ?? 'The host'} starts the game when there are enough players.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {children}
        <ul className="space-y-1.5">
          {seats.map((m, i) => (
            <li
              key={m?.id ?? `seat-${i}`}
              className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
            >
              {m ? (
                <>
                  <span className="font-medium">{names[m.user_id] ?? 'Someone'}</span>
                  {m.user_id === lobby.host_id && (
                    <Badge variant="muted">
                      <Crown className="mr-1 h-3 w-3" /> host
                    </Badge>
                  )}
                  {m.user_id === userId && <Badge variant="outline">you</Badge>}
                </>
              ) : (
                <span className="text-muted-foreground">Open seat</span>
              )}
            </li>
          ))}
        </ul>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex flex-wrap items-center gap-2">
          {isHost ? (
            <>
              <Button onClick={() => void call('start_lobby')} disabled={busy}>
                Start with {members.length} {members.length === 1 ? 'player' : 'players'}
              </Button>
              <Button variant="ghost" onClick={() => void call('leave_lobby', () => navigate('/games'))} disabled={busy}>
                Call it off
              </Button>
            </>
          ) : isIn ? (
            <>
              <p className="text-sm text-muted-foreground">You're in. Waiting for the host to start…</p>
              <Button variant="ghost" onClick={() => void call('leave_lobby', () => navigate('/games'))} disabled={busy}>
                Leave
              </Button>
            </>
          ) : full ? (
            <p className="text-sm text-muted-foreground">This game is full.</p>
          ) : (
            <Button onClick={() => void call('join_lobby')} disabled={busy}>
              Join
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
